"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { MotionConfig, motion } from "framer-motion"
import { ArrowRight, Phone, MapPin, Clock, Smartphone, Flame, ShoppingBag } from "lucide-react"
import type { Restaurant } from "@/lib/restaurants/types"
import { mariaBonitaFactos, mariaBonitaMapa, mariaBonitaHorario } from "@/lib/restaurants/maria-bonita"
import { ChapeuCangaceiro, FilaCactos, Bandeiras } from "./maria-bonita-art"
import { EstadoAgora } from "./maria-bonita-live"

/* Curva ease-out forte (Emil Kowalski). Nunca ease-in em UI. */
const OUT = [0.23, 1, 0.32, 1] as const

const ICONS = { Smartphone, Flame, ShoppingBag } as const

/** Segunda → domingo (o array do horário está indexado 0 = domingo). */
const ORDEM_SEMANA = [1, 2, 3, 4, 5, 6, 0]

/** Dia de hoje em Lisboa. `null` até montar, para não partir a hidratação. */
function useHojeEmLisboa() {
  const [hoje, setHoje] = useState<number | null>(null)
  useEffect(() => {
    const nome = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Lisbon",
      weekday: "short",
    }).format(new Date())
    setHoje(["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(nome))
  }, [])
  return hoje
}

export function MariaBonitaLanding({ slug, r }: { slug: string; r: Restaurant }) {
  const t = r.theme
  const L = r.landing
  const hoje = useHojeEmLisboa()

  /**
   * `prefers-reduced-motion` trata-se com <MotionConfig reducedMotion="user">
   * e não com um hook no valor inicial: um hook só resolve no cliente e
   * partiria a hidratação (SSR renderiza um transform, o cliente outro).
   */

  /** Entrada escalonada. */
  const entra = (i: number) => ({
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.45, delay: i * 0.06, ease: OUT },
  })

  /** Revelação ao entrar no ecrã, uma única vez. */
  const revela = (i = 0) => ({
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, delay: i * 0.06, ease: OUT },
  })

  return (
    <MotionConfig reducedMotion="user">
    <div className="bonita-body" style={{ backgroundColor: t.bg, color: t.ink }}>
      {/* ============================ HERO ============================ */}
      <section className="relative overflow-hidden">
        {/* sol do sertão */}
        <div
          aria-hidden
          className="bonita-sol pointer-events-none absolute inset-0"
          style={{
            background: `radial-gradient(120% 80% at 50% -10%, ${t.bgWarm} 0%, transparent 62%)`,
          }}
        />

        <div className="relative max-w-5xl mx-auto px-5 pt-10 pb-0 sm:pt-14">
          <motion.div {...entra(0)} className="flex justify-center">
            <ChapeuCangaceiro className="h-12 sm:h-14 w-auto" ink={t.ink} star={t.accent} />
          </motion.div>

          <motion.div {...entra(1)} className="mt-5 flex justify-center">
            <EstadoAgora fallback={L.heroPill} ink={t.ink} hairline={t.hairline} />
          </motion.div>

          <motion.h1
            {...entra(2)}
            className="bonita-display mt-6 text-center"
            style={{
              fontSize: "clamp(40px, 10vw, 92px)",
              lineHeight: 0.92,
              letterSpacing: "-0.02em",
              color: t.ink,
              textWrap: "balance",
            }}
          >
            {L.heroHeadlineBefore}
            <span className="relative inline-block" style={{ color: t.brass }}>
              {L.heroHeadlineHighlight}
              {/* sublinhado desenhado à mão */}
              <svg
                viewBox="0 0 240 16"
                aria-hidden
                className="absolute left-0 -bottom-1 w-full h-[0.16em]"
                preserveAspectRatio="none"
              >
                <path
                  d="M3 11C48 5 96 4 140 6c34 1.5 62 4 97 7"
                  stroke={t.accent}
                  strokeWidth="7"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </span>
            {L.heroHeadlineAfter}
          </motion.h1>

          <motion.p
            {...entra(3)}
            className="mt-6 mx-auto text-center"
            style={{
              maxWidth: "46ch",
              fontSize: "clamp(15px, 2.1vw, 18px)",
              lineHeight: 1.65,
              color: t.inkMuted,
            }}
          >
            {L.heroSubtitle}
          </motion.p>

          <motion.div
            {...entra(4)}
            className="mt-8 flex flex-wrap items-center justify-center gap-3"
          >
            <Link
              href={`/r/${slug}/ementa`}
              className="bonita-press bonita-outline bonita-display inline-flex items-center gap-2.5 rounded-2xl px-7"
              style={{
                minHeight: 54,
                backgroundColor: t.primary,
                color: "#F7EEDD",
                fontSize: 18,
                letterSpacing: "0.01em",
              }}
            >
              {L.ctaEmenta}
              <ArrowRight size={19} strokeWidth={2.6} aria-hidden />
            </Link>

            <a
              href={`tel:${r.telefone.replace(/\s/g, "")}`}
              className="bonita-press bonita-outline inline-flex items-center gap-2.5 rounded-2xl px-6 font-semibold"
              style={{
                minHeight: 54,
                backgroundColor: "#F7EEDD",
                color: t.ink,
                fontSize: 16,
              }}
            >
              <Phone size={17} strokeWidth={2.4} aria-hidden />
              Ligar
            </a>
          </motion.div>

          {/* A roulote, emoldurada como um cartaz */}
          <motion.figure
            {...entra(5)}
            className="mt-12 mx-auto w-full max-w-[300px] sm:max-w-[340px]"
          >
            <div
              className="bonita-outline rounded-[22px] overflow-hidden"
              style={{ backgroundColor: t.card, transform: "rotate(-1.6deg)" }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={L.heroImg}
                alt="A roulote Maria Bonita, em Bombarral, com a esplanada montada."
                className="block w-full h-auto"
                loading="eager"
              />
              <figcaption
                className="bonita-hand px-4 py-2.5 text-center"
                style={{ color: "#F7EEDD", fontSize: 19 }}
              >
                {L.galleryLabel}
              </figcaption>
            </div>
          </motion.figure>
        </div>

        {/* horizonte de cactos */}
        <div className="relative mt-8">
          <FilaCactos className="px-2 overflow-hidden" color={t.ink} opacity={0.82} />
          <div className="h-3" style={{ backgroundColor: t.ink }} />
        </div>
      </section>

      {/* ======================= FAIXA ROLANTE ======================= */}
      <section
        aria-hidden
        className="overflow-hidden py-3"
        style={{ backgroundColor: t.ink }}
      >
        <div className="bonita-marquee-track">
          {[0, 1].map((copia) => (
            <div key={copia} className="flex shrink-0">
              {Array.from({ length: 6 }).map((_, i) => (
                <span
                  key={i}
                  className="bonita-display flex items-center gap-6 px-6 whitespace-nowrap"
                  style={{ color: t.accent, fontSize: 20, letterSpacing: "0.04em" }}
                >
                  {L.footerQuote}
                  <span style={{ color: t.brass }}>✦</span>
                  MARIA BONITA
                  <span style={{ color: t.brass }}>✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* ========================== FACTOS ========================== */}
      <section className="max-w-5xl mx-auto px-5 py-12 sm:py-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {mariaBonitaFactos.map((f, i) => (
            <motion.div
              key={f.label}
              {...revela(i)}
              className="bonita-outline-sm rounded-2xl px-5 py-5"
              style={{ backgroundColor: t.card }}
            >
              <p
                className="bonita-label"
                style={{ color: t.accent, letterSpacing: "0.22em", fontSize: 10 }}
              >
                {f.label}
              </p>
              <p
                className="bonita-display mt-2"
                style={{ color: "#F7EEDD", fontSize: 30, lineHeight: 1 }}
              >
                {f.valor}
              </p>
              <p className="mt-1.5" style={{ color: "rgba(247,238,221,0.72)", fontSize: 13 }}>
                {f.nota}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ====================== O QUE SAI DA CHAPA ====================== */}
      <section className="max-w-5xl mx-auto px-5 pb-4">
        <motion.header {...revela(0)} className="text-center">
          <p className="bonita-label" style={{ color: t.inkMuted }}>
            {L.picksLabel}
          </p>
          <h2
            className="bonita-display mt-2.5"
            style={{ fontSize: "clamp(30px, 6vw, 50px)", lineHeight: 1, color: t.ink }}
          >
            {L.galleryTitle}
          </h2>
        </motion.header>

        <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {L.gallery.map((g, i) => (
            <motion.article
              key={g.src}
              {...revela(i)}
              className="bonita-card bonita-outline rounded-[22px] overflow-hidden flex flex-col h-full"
              style={{ backgroundColor: t.card }}
            >
              {/* proporção fixa: as quatro fotos vêm do Instagram com formatos
                  diferentes e sem isto as fichas ficavam desalinhadas */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={g.src}
                alt={g.alt}
                className="block w-full aspect-[16/10] object-cover"
                loading="lazy"
                decoding="async"
              />
              <div className="px-5 py-4 flex items-center justify-between gap-3 mt-auto">
                <h3 className="bonita-display" style={{ color: "#F7EEDD", fontSize: 21 }}>
                  {g.label}
                </h3>
                <span
                  className="bonita-label bonita-selo shrink-0 rounded px-2 py-1"
                  style={{
                    color: t.accent,
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    border: `1.5px solid ${t.accent}`,
                  }}
                >
                  Na chapa
                </span>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div {...revela(1)} className="mt-9 flex justify-center">
          <Link
            href={`/r/${slug}/ementa`}
            className="bonita-press bonita-outline bonita-display inline-flex items-center gap-2.5 rounded-2xl px-7"
            style={{
              minHeight: 54,
              backgroundColor: t.accent,
              color: t.ink,
              fontSize: 18,
            }}
          >
            {L.ctaEmenta}
            <ArrowRight size={19} strokeWidth={2.6} aria-hidden />
          </Link>
        </motion.div>
      </section>

      {/* ======================= COMO FUNCIONA ======================= */}
      <section className="max-w-5xl mx-auto px-5 py-14 sm:py-20">
        <motion.header {...revela(0)} className="text-center">
          <h2
            className="bonita-display"
            style={{ fontSize: "clamp(28px, 5.5vw, 44px)", lineHeight: 1, color: t.ink }}
          >
            {L.stepsTitle}
          </h2>
          <p className="mt-3 mx-auto" style={{ maxWidth: "44ch", color: t.inkMuted, fontSize: 15.5 }}>
            {L.stepsLead}
          </p>
        </motion.header>

        <ol className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
          {L.steps.map((s, i) => {
            const Icone = ICONS[s.iconName as keyof typeof ICONS] ?? Smartphone
            return (
              <motion.li
                key={s.n}
                {...revela(i)}
                className="bonita-outline-sm rounded-2xl p-6"
                style={{ backgroundColor: "rgba(247, 238, 221, 0.66)" }}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="bonita-display"
                    style={{ fontSize: 40, lineHeight: 1, color: t.brass }}
                  >
                    {s.n}
                  </span>
                  <Icone size={24} strokeWidth={2} aria-hidden style={{ color: t.ink }} />
                </div>
                <h3 className="bonita-display mt-4" style={{ fontSize: 20, color: t.ink }}>
                  {s.title}
                </h3>
                <p className="mt-2" style={{ color: t.inkMuted, fontSize: 14.5, lineHeight: 1.62 }}>
                  {s.body}
                </p>
              </motion.li>
            )
          })}
        </ol>
      </section>

      {/* ====================== ONDE E QUANDO ====================== */}
      <section className="max-w-5xl mx-auto px-5 pb-4">
        <motion.header {...revela(0)} className="text-center">
          <p className="bonita-label" style={{ color: t.inkMuted }}>
            Onde nos encontras
          </p>
          <h2
            className="bonita-display mt-2.5"
            style={{ fontSize: "clamp(28px, 5.5vw, 44px)", lineHeight: 1, color: t.ink }}
          >
            A roulote está em Bombarral
          </h2>
        </motion.header>

        <div className="mt-9 grid grid-cols-1 lg:grid-cols-5 gap-5 items-stretch">
          {/* mapa */}
          <motion.div
            {...revela(0)}
            className="bonita-outline rounded-[22px] overflow-hidden lg:col-span-3"
            style={{ backgroundColor: t.card, minHeight: 320 }}
          >
            <iframe
              title={`Mapa — ${r.nome}, Bombarral`}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${mariaBonitaMapa.bbox}&layer=mapnik&marker=${mariaBonitaMapa.lat},${mariaBonitaMapa.lon}`}
              className="block w-full h-full"
              style={{ border: 0, minHeight: 320 }}
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          </motion.div>

          {/* horário da semana */}
          <motion.div
            {...revela(1)}
            className="bonita-outline rounded-[22px] p-6 lg:col-span-2 flex flex-col"
            style={{ backgroundColor: t.card }}
          >
            <p
              className="bonita-label"
              style={{ color: t.accent, fontSize: 10, letterSpacing: "0.2em" }}
            >
              Horário
            </p>

            <ul className="mt-4 space-y-1">
              {ORDEM_SEMANA.map((idx) => {
                const d = mariaBonitaHorario.find((h) => h.dia === idx)!
                const ehHoje = hoje === idx
                return (
                  <li
                    key={d.dia}
                    className="flex items-center justify-between gap-3 rounded-lg px-2.5 py-2"
                    style={{
                      backgroundColor: ehHoje ? "rgba(242, 194, 48, 0.16)" : "transparent",
                      border: `1.5px solid ${ehHoje ? t.accent : "transparent"}`,
                    }}
                  >
                    <span
                      style={{
                        color: ehHoje ? t.accent : "rgba(247,238,221,0.86)",
                        fontSize: 14.5,
                        fontWeight: ehHoje ? 600 : 400,
                      }}
                    >
                      {d.nome}
                      {ehHoje && <span className="bonita-hand ml-2">hoje</span>}
                    </span>
                    <span
                      style={{
                        color: d.fechado ? "rgba(247,238,221,0.5)" : "#F7EEDD",
                        fontSize: 14,
                      }}
                    >
                      {d.fechado ? "Fechado" : "19h00 – 00h00"}
                    </span>
                  </li>
                )
              })}
            </ul>

            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mariaBonitaMapa.query)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bonita-press bonita-display mt-6 inline-flex items-center justify-center gap-2.5 rounded-2xl px-5"
              style={{
                minHeight: 50,
                backgroundColor: t.accent,
                color: t.ink,
                border: `2px solid ${t.ink}`,
                fontSize: 16,
              }}
            >
              <MapPin size={18} strokeWidth={2.4} aria-hidden />
              Como chegar
            </a>
          </motion.div>
        </div>
      </section>

      {/* ========================== A CASA ========================== */}
      <section className="max-w-5xl mx-auto px-5 pb-16">
        <motion.div
          {...revela(0)}
          className="bonita-outline rounded-[26px] overflow-hidden"
          style={{ backgroundColor: t.ink }}
        >
          <div className="px-6 sm:px-10 py-10 text-center">
            <div className="flex justify-center">
              <ChapeuCangaceiro className="h-11 w-auto" ink={t.accent} star={t.brass} />
            </div>

            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={r.logoSrc}
              alt={r.nome}
              className="mx-auto mt-5 h-14 sm:h-16 w-auto"
              loading="lazy"
            />

            <div className="mt-3 flex justify-center">
              <Bandeiras />
            </div>

            <dl className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6 text-left sm:text-center">
              {[
                { Icone: MapPin, termo: "Onde", desc: r.morada },
                { Icone: Clock, termo: "Quando", desc: r.horario },
                { Icone: Phone, termo: "Contacto", desc: r.telefone },
              ].map(({ Icone, termo, desc }) => (
                <div key={termo} className="flex sm:flex-col items-start sm:items-center gap-3">
                  <Icone size={20} strokeWidth={2} aria-hidden style={{ color: t.accent }} />
                  <div>
                    <dt
                      className="bonita-label"
                      style={{ color: t.accent, fontSize: 10, letterSpacing: "0.2em" }}
                    >
                      {termo}
                    </dt>
                    <dd className="mt-1.5" style={{ color: "#F7EEDD", fontSize: 15.5 }}>
                      {desc}
                    </dd>
                  </div>
                </div>
              ))}
            </dl>

            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <a
                href={`https://wa.me/${(r.whatsapp ?? "").replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bonita-press inline-flex items-center gap-2.5 rounded-2xl px-6 font-semibold"
                style={{
                  minHeight: 50,
                  backgroundColor: t.fire,
                  color: t.ink,
                  border: `2px solid ${t.accent}`,
                  fontSize: 15.5,
                }}
              >
                Falar connosco
              </a>
              <Link
                href={`/r/${slug}/ementa`}
                className="bonita-press inline-flex items-center gap-2.5 rounded-2xl px-6 font-semibold"
                style={{
                  minHeight: 50,
                  backgroundColor: "transparent",
                  color: t.accent,
                  border: `2px solid ${t.accent}`,
                  fontSize: 15.5,
                }}
              >
                Ver a ementa
              </Link>
            </div>
          </div>

          <FilaCactos className="pb-0 -mb-px overflow-hidden" color={t.accent} opacity={0.28} />
        </motion.div>

        <p
          className="bonita-hand mt-9 text-center"
          style={{ color: t.ink, fontSize: "clamp(22px, 4vw, 30px)" }}
        >
          {L.footerQuote}
        </p>
        <p className="bonita-label mt-3 text-center" style={{ color: t.inkMuted, fontSize: 10 }}>
          {L.footerCopyright}
        </p>
      </section>
    </div>
    </MotionConfig>
  )
}
